<template>
  <div class="card p-10">
    <RequiredText></RequiredText>
    <form id="paymentForm" @submit.prevent="submitForm">
      <Card class="card shadow p-5 mb-10">
        <div class="row">
          <InputDate
              containerClass="col-12 col-md-4 mt-auto mb-5"
              name="payment-date"
              type="text"
              :label="$t('payment.date')"
              labelClass="form-label fs-6 text-black"
              :inputClass="'form-control form-control-sm form-control-solid'"
              :isRequired="true" size="lg"
              :placeholder="$t('payment.date')"
              v-model="formData.payment_date"
          />

          <Select2
              :is-required="true"
              containerClass="form-group col-12 col-md-4 mt-auto mb-5"
              :multiple="false"
              :show-search="true"
              :selectClass="'form-select form-control-lg form-select-solid'"
              :name="`payment-contact`"
              :label="$t('payment.customer')"
              label-class="form-label"
              :placeholder-text="$t('payment.customer')"
              v-model="contact"
          >
            <option v-for="contact in filteredContacts" :value="contact.id" :key="contact.id">
              {{ contact.lastname }} {{ contact.firstname }}
            </option>
          </Select2>

          <Select2
              container-class="form-group col-12 col-md-4 mt-auto mb-5"
              :multiple="false"
              :show-search="true"
              :selectClass="'form-select form-control-sm form-select-solid'"
              :name="`payment-invoice_number`"
              :label="$t('payment.invoice_number')"
              labelClass="form-label fs-6 text-black"
              :placeholder-text="$t('payment.invoice_number')"
              v-model="selectedInvoice"
          >
            <option v-for="(invoice, index) in filteredInvoices" :key="index" :value="invoice.id">
              {{ invoice.invoice_number }} ( {{ invoice.total}} {{ invoice?.currency?.symbol }} )
            </option>
          </Select2>


        </div>
        <div class="row">
          <Select2
              container-class="form-group col-12 col-md-4 mt-auto mb-5"
              :multiple="false"
              :show-search="true"
              :selectClass="'form-select form-control-sm form-select-solid'"
              :name="`payment-pay_mode`"
              :label="$t('payment.pay_mode')"
              labelClass="form-label fs-6 text-black"
              :placeholder-text="$t('payment.pay_mode')"
              v-model="formData.payment_type_id"
          >
            <option v-for="paymentType in paymentTypes" :value="paymentType.id" :key="paymentType.id">
              {{ paymentType.name }}
            </option>
          </Select2>
          <Input
              containerClass="col-12 col-md-4 mt-auto mb-5"
              name="payment-amount"
              type="text"
              :label="$t('payment.amount')"
              labelClass="form-label fs-6 text-black"
              :inputClass="'form-control form-control-solid border-start pe-10'"
              :isRequired="true" size="lg"
              v-model="formData.amount"
              readonly
              :placeholder="$t('payment.amount')"
          >
            <template #rightAddon>
              <span class="position-absolute end-0 top-50 translate-middle-y me-5 text-muted fw-bolder"> {{ invoiceCurrency?.symbol }} </span>
            </template>
          </Input>
          <InputFileIcon
              containerClass="col-12 col-md-4 mt-auto mb-5"
              name="payment.file"
              :label="$t('payment.justifyFile')"
              labelClass="form-label fs-6 text-black"
              :inputClass="'form-control form-control-sm form-control-solid'"
              :isRequired="true"
              size="lg"
              :placeholder="$t('payment.file')"
              v-model="file"
          />
        </div>
      </Card>

      <Card class="card shadow h-100 p-5">
        <div class="row">
          <div class="col-md-6">
              <cash-deposit v-model="formData" :source-currency="invoiceCurrency" id="paymentForm"></cash-deposit>
          </div>

          <div class="col-md-6">
            <CKEditor
                labelClass="form-label fs-6 text-black"
                :label="$t('payment.addNote')"
                :required="false"
                id="payment-editor"
                :content="formData.notes"
                @text="getNote"
            />
          </div>
        </div>
      </Card>

      <div class="d-flex justify-content-end mt-10">
        <div class="me-2">
          <Button type="cancel" @click="cancel" btn-class="btn-sm" btn-text="button.cancel" size="7px 30px"></Button>
        </div>
        <div>
          <Button type="submit" btn-class="btn-sm" btn-text="button.save" size="7px 30px" :isLoading="submitting"></Button>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import {useStore} from "vuex";
import {computed, defineProps, watch, ref, onMounted} from "vue";
import InputDate from "@/components/InputDate.vue";
import Select2 from "@/components/Select2.vue";
import Input from "@/components/Input.vue";
import Card from "@/Modules/CRM/components/Card.vue";
import CKEditor from "@/components/CKEditor.vue";
import Button from "@/components/Button.vue";
import { useRouter } from 'vue-router';
import InputFileIcon from "@/components/InputFileIcon"
import RequiredText from "@/components/RequiredText.vue"
import CashDeposit from "@/Modules/CRM/views/Payment/components/CashDeposit.vue";

const store = useStore()
const router = useRouter()

const props = defineProps({
  payment: {
    type: Object,
    default: () => ({
      id: null,
      payment_date: new Date(),
      notes: '',
      invoice_id: '',
      payment_type_id: '',
      advance_invoice_id: '',
      type: 'amount',
      amount: 0,
      exchange_rate: '',
      percentage: 0,
      payment_account_type: 'account',
      account_id: '',
      box_id: '',
      files: []
    })
  },
  submitForm: Function,
  submitting: {type: Boolean, default: false}
})

const formData = ref(props.payment)
const contact = ref('')
const file = ref(null)
const filteredInvoices = ref([]);
const advanceInvoices = ref([]);
const selectedInvoice = ref('')

const paymentTypes = computed(() => store.getters['payments/paymentTypes'])
const invoices = computed(() => store.getters['invoices/invoices'])
const contacts = computed(() => store.getters['contacts/getContacts'])
const invoiceCurrency = computed(() => filteredInvoices.value?.find(x=> x.id === formData.value.invoice_id)?.currency)

const contactIdsWithInvoices = computed(() => {
  return Array.from(new Set(invoices.value.map(invoice => invoice?.contact_id)));
});
const filteredContacts = computed(() => {
  return contacts.value.filter(contact =>
      contactIdsWithInvoices.value.includes(contact.id)
  );
});

watch(() => selectedInvoice.value, (value) => {
  if (value) {
    let invoiceFilter = invoices.value?.find(invoice => invoice.id == value);
    let isAdvance = false;

    if (!invoiceFilter) {
      invoiceFilter = advanceInvoices.value?.find(advance => advance.id == value);
      isAdvance = !!invoiceFilter;
    }

    if (invoiceFilter) {
      formData.value.amount = invoiceFilter?.remaining_payment ?? invoiceFilter?.total;
      formData.value.payment_type_id = invoiceFilter?.payment_type_id;

      if (isAdvance) {
        formData.value.advance_invoice_id = invoiceFilter.id;
        formData.value.invoice_id = invoiceFilter.invoice_id;
        formData.value.advancePayment = true;
      } else {
        formData.value.invoice_id = invoiceFilter.id;
        formData.value.advance_invoice_id = null;
      }
    }
  }
});

watch(contact, (newContact) => {
  if (newContact) {
    const filteredByContact = [
      ...invoices.value.filter(invoice => invoice?.contact_id === newContact),
      ...advanceInvoices.value.filter(advance => advance?.invoice?.contact_id === newContact)
    ];
    filteredInvoices.value = filteredByContact;
    formData.value.amount = 0;
    formData.value.payment_type_id = null;
  } else {
    filteredInvoices.value = [];
  }
});

watch(() => file.value, (value) => {
  if(value){
    formData.value.files.push(value)
  }
});

function cancel(){
  router.push({ name: 'payments'})
}

function getNote(text) {
  formData.value.notes = text
}

onMounted(() => {
  store.dispatch('currencies/loadCurrencies')
  store.dispatch('contacts/loadContacts')
  store.dispatch("accounts/loadTransactionTypesEnum")
  store.dispatch('invoices/getAdvanceInvoices').then((resp) => {
    advanceInvoices.value = resp.data
  })
})
</script>

<style scoped>

</style>

